import React, { useEffect } from 'react';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import IGlassArea from 'components/interfaces/IGlassArea';
import IGlassAreaMetaData from 'components/interfaces/IGlassAreaMetaData';
import IWall from 'components/interfaces/IWall';
import roundTwoDecimals from 'services/numbers/roundTwoDecimals';
import translations from 'services/translations/translations';
import validGlassProperties from 'services/constants/validGlassProperties';

import {
  MANUAL,
  MAX_WEIGHT,
  QUANTITY,
} from 'services/constants/glassWidthCalculationTypes';

import getGlassWidths from '../getGlassWidths';
import GlassRow from './GlassRow';
import calculateManualGlassLastGlassLength from './calculateManualGlassLastGlassLength';

const GlassesList = ({
  glassArea,
  glassAreaLength,
  glassAreaMetaData,
  setGlassArea,
  wall,
  disabledInputs,
}: {
  glassArea: IGlassArea;
  glassAreaLength: number;
  glassAreaMetaData: IGlassAreaMetaData;
  setGlassArea: (area: IGlassArea) => void;
  wall: IWall;
  disabledInputs?: boolean;
}): JSX.Element | null => {
  const { t } = useTranslation([translations.common]);

  const disabledWidths = glassAreaMetaData.calculationType !== MANUAL;

  const calculateLastGlass = () => {
    if (disabledWidths) {
      return;
    }

    const lastGlassIndex = glassArea.glasses.length - 1;

    const lastGlassAreaLength = calculateManualGlassLastGlassLength(
      glassArea,
      glassAreaLength
    );

    if (lastGlassAreaLength !== glassArea.glasses[lastGlassIndex].width) {
      setGlassArea({
        ...glassArea,
        glasses: glassArea.glasses.map((glass, index) => {
          if (index !== lastGlassIndex) {
            return glass;
          }

          return {
            ...glassArea.glasses[lastGlassIndex],
            width: lastGlassAreaLength,
          };
        }),
      });
    }
  };

  useEffect(() => {
    calculateLastGlass();
  }, [glassArea.glasses, glassAreaMetaData.calculationType]);

  const getGlasses = () => {
    if ([MAX_WEIGHT, QUANTITY].includes(glassAreaMetaData.calculationType)) {
      if (glassAreaMetaData.calculationType === MAX_WEIGHT) {
        if (glassAreaMetaData.maxWeight === null) {
          return null;
        }

        if (glassAreaMetaData.maxWeight > validGlassProperties.maxWeight.max) {
          return null;
        }

        if (glassAreaMetaData.maxWeight < validGlassProperties.maxWeight.min) {
          return null;
        }
      }

      if (glassAreaMetaData.calculationType === QUANTITY) {
        if (glassAreaMetaData.quantity === null) {
          return null;
        }

        if (glassAreaMetaData.quantity > validGlassProperties.quantity.max) {
          return null;
        }

        if (glassAreaMetaData.quantity < validGlassProperties.quantity.min) {
          return null;
        }
      }
    }

    if (
      [MANUAL, MAX_WEIGHT, QUANTITY].includes(glassAreaMetaData.calculationType)
    ) {
      const glassWidths = getGlassWidths(
        glassAreaLength,
        wall,
        glassArea,
        glassAreaMetaData
      );

      return glassWidths.map((glassWidth, index) => (
        <GlassRow
          disabledWidths={disabledWidths}
          glass={{ width: roundTwoDecimals(glassWidth) }}
          glassArea={glassArea}
          index={index}
          key={`glass-row-${index}`}
          setGlassArea={setGlassArea}
          wall={wall}
          disabledInputs={disabledInputs}
        />
      ));
    }

    return null;
  };

  return (
    <>
      <hr />
      <Row className="glass-area-glass-header">
        <Col>
          <h3>Glasses</h3>
        </Col>
      </Row>
      <Row className="justify-content-md-center">
        <Col xs="3">
          <h5>{t(`${translations.common}:Name`)}</h5>
        </Col>
        <Col>
          <h5>{t(`${translations.common}:Length (mm)`)}</h5>
        </Col>
      </Row>
      {getGlasses()}
    </>
  );
};

export default GlassesList;
